import { type FC, useEffect, useRef, useState } from "react";
import {
  AppstoreOutlined,
  ArrowLeftOutlined,
  AudioMutedOutlined,
  AudioOutlined,
  CheckOutlined,
  DisconnectOutlined,
  MessageOutlined,
  PhoneOutlined,
  SendOutlined,
} from "@ant-design/icons";
import { Button, Input, Spin } from "antd";
import classNames from "classnames";
import {
  formatCallDuration,
  type PatientCallPhase,
} from "./useTwilioPatientCall";
import { timeProvider } from "../../AppController";
import styles from "./styles.module.scss";

const DIAL_KEYS = [
  { digit: "1", letters: "" },
  { digit: "2", letters: "ABC" },
  { digit: "3", letters: "DEF" },
  { digit: "4", letters: "GHI" },
  { digit: "5", letters: "JKL" },
  { digit: "6", letters: "MNO" },
  { digit: "7", letters: "PQRS" },
  { digit: "8", letters: "TUV" },
  { digit: "9", letters: "WXYZ" },
  { digit: "*", letters: "" },
  { digit: "0", letters: "+" },
  { digit: "#", letters: "" },
] as const;

const WAVE_FACTORS = [0.35, 0.65, 0.9, 0.55, 1, 0.7, 0.45, 0.8, 0.6, 0.95];
export const WELCOME_MESSAGE_TEMPLATE =
  "Hello, this is Circadia Health.\n\nWe're partnering with Providence at Home with Compassus to install a remote patient monitoring device that monitors your respiration and heart rate at home.\n\nPlease expect a call within the next hour from (833) 602-1830 to share more and answer any questions. If that is not a good time, reply here and we'll reschedule.";
const MESSAGE_TEMPLATES = [
  {
    label: "Calling you shortly",
    description: "Heads-up before the welcome call",
    body: WELCOME_MESSAGE_TEMPLATE,
    icon: "call",
  },
] as const;

export const normalizePhoneDigits = (
  value: string | null | undefined
): string => (typeof value === "string" ? value.replace(/\D/g, "") : "");

export const formatDialpadPhone = (value: string): string => {
  if (/[*#]/.test(value)) return value;
  const digits = normalizePhoneDigits(value).slice(0, 11);
  const nationalDigits =
    digits.length === 11 && digits.startsWith("1") ? digits.slice(1) : digits;
  if (nationalDigits.length === 0) return "";
  if (nationalDigits.length <= 3) return `(${nationalDigits}`;
  if (nationalDigits.length <= 6) {
    return `(${nationalDigits.slice(0, 3)}) ${nationalDigits.slice(3)}`;
  }
  return `(${nationalDigits.slice(0, 3)}) ${nationalDigits.slice(
    3,
    6
  )}-${nationalDigits.slice(6, 10)}`;
};

export const phoneMatchesPatientRecord = (
  phone: string,
  patientPhoneNumbers: Array<string | null | undefined>
): boolean => {
  const entered = normalizePhoneDigits(phone).slice(-10);
  return (
    entered.length === 10 &&
    patientPhoneNumbers.some(
      (candidate) => normalizePhoneDigits(candidate).slice(-10) === entered
    )
  );
};

export interface DialerContactSuggestion {
  key: string;
  name: string;
  description?: string | null;
  phoneNumber: string;
  actionLabel?: string;
  onClick?: () => void;
}

interface PatientCallDialerProps {
  phone: string;
  onPhoneChange: (value: string) => void;
  patientPhoneNumbers: string[];
  isNumbersLoading: boolean;
  canCall: boolean;
  phase: PatientCallPhase;
  statusLabel: string;
  elapsedSeconds: number;
  volume: { input: number; output: number };
  errorMessage: string | null;
  isActive: boolean;
  isMuted: boolean;
  wasConnected: boolean;
  patientName: string;
  onStartCall: () => void;
  onHangUp: () => void;
  onSendDigit: (digit: string) => boolean;
  onToggleMute: () => void;
  onReset: () => void;
  smsThread?: ICompassusPatientSmsThreadResponse | null;
  isSmsLoading?: boolean;
  isSendingText?: boolean;
  onSendText?: (body: string) => Promise<boolean>;
  isUpdatingPrimaryNumber?: boolean;
  onUpdatePrimaryNumber?: () => Promise<boolean>;
  isPrimaryNumber?: boolean;
  initialTextBody?: string;
  hideDialpad?: boolean;
  hideMessages?: boolean;
  hideStatus?: boolean;
  circularCallButton?: boolean;
  reserveSuggestionSpace?: boolean;
  contactSuggestions?: DialerContactSuggestion[];
  onSelectSuggestion?: (phoneNumber: string) => void;
}

export const PatientCallDialer: FC<PatientCallDialerProps> = ({
  phone,
  onPhoneChange,
  patientPhoneNumbers,
  isNumbersLoading,
  canCall,
  phase,
  statusLabel,
  elapsedSeconds,
  volume,
  errorMessage,
  isActive,
  isMuted,
  wasConnected,
  patientName,
  onStartCall,
  onHangUp,
  onSendDigit,
  onToggleMute,
  onReset,
  smsThread = null,
  isSmsLoading = false,
  isSendingText = false,
  onSendText,
  isUpdatingPrimaryNumber = false,
  onUpdatePrimaryNumber,
  isPrimaryNumber = false,
  initialTextBody = "",
  hideDialpad = false,
  hideMessages = false,
  hideStatus = false,
  circularCallButton = false,
  reserveSuggestionSpace = false,
  contactSuggestions = [],
  onSelectSuggestion,
}) => {
  const [showDtmf, setShowDtmf] = useState(false);
  const [textBody, setTextBody] = useState(initialTextBody);
  const threadRef = useRef<HTMLDivElement | null>(null);
  const matchesRecord = phoneMatchesPatientRecord(phone, patientPhoneNumbers);
  const isConnected = phase === "connected" || phase === "reconnecting";
  const isReady = phase === "idle";
  const isEnded = !isReady && !isActive;
  const waveLevel = Math.max(volume.input, volume.output);
  const initials =
    patientName
      .split(/\s+/)
      .filter(Boolean)
      .slice(0, 2)
      .map((part) => part[0]?.toUpperCase())
      .join("") || "PT";

  useEffect(() => {
    if (!isConnected) setShowDtmf(false);
  }, [isConnected]);

  useEffect(() => {
    const thread = threadRef.current;
    if (thread) thread.scrollTop = thread.scrollHeight;
  }, [smsThread?.messages.length]);

  const addDigit = (digit: string) => {
    if (isActive) {
      if (isConnected) onSendDigit(digit);
      return;
    }
    onPhoneChange(formatDialpadPhone(`${phone}${digit}`));
  };

  const removeDigit = () => {
    if (isActive) return;
    const digits = normalizePhoneDigits(phone).slice(0, -1);
    onPhoneChange(formatDialpadPhone(digits));
  };

  const handleSendText = async () => {
    const body = textBody.trim();
    if (!body || !onSendText) return;
    if (await onSendText(body)) setTextBody("");
  };

  const messageCount = textBody.length;
  const smsSegments = Math.max(1, Math.ceil(messageCount / 160));
  const hasDialableNumber =
    normalizePhoneDigits(phone).slice(-10).length === 10;
  const showDialerMeta =
    matchesRecord ||
    isPrimaryNumber ||
    (hasDialableNumber && Boolean(onUpdatePrimaryNumber));
  const hasMessageHistory = Boolean(smsThread?.messages.length);
  const patientFirstName = patientName.trim().split(/\s+/)[0] || "the patient";
  const renderDialerMeta = () =>
    showDialerMeta ? (
      <div className={styles.wp2DialerMetaRow}>
        {matchesRecord ? (
          <div className={styles.wp2PhoneHint}>
            <CheckOutlined /> Matches patient record
          </div>
        ) : (
          <span />
        )}
        {isPrimaryNumber ? (
          <div className={styles.wp2PhoneHint}>
            <CheckOutlined /> Primary
          </div>
        ) : hasDialableNumber && onUpdatePrimaryNumber ? (
          <button
            type="button"
            className={styles.wp2UpdatePrimaryButton}
            disabled={isUpdatingPrimaryNumber}
            onClick={() => void onUpdatePrimaryNumber()}
          >
            {isUpdatingPrimaryNumber ? "Updating…" : "+ Set as primary"}
          </button>
        ) : (
          <span />
        )}
      </div>
    ) : null;

  const templateIcon = (icon: (typeof MESSAGE_TEMPLATES)[number]["icon"]) => {
    if (icon === "call") return <PhoneOutlined />;
    return <MessageOutlined />;
  };

  const renderKeypad = (dtmf = false) => (
    <div
      className={classNames(styles.wp2Dialpad, {
        [styles.wp2DialpadDtmf]: dtmf,
      })}
      aria-label={dtmf ? "In-call keypad" : "Phone keypad"}
    >
      {DIAL_KEYS.map(({ digit, letters }) => (
        <button
          type="button"
          key={digit}
          className={styles.wp2DialKey}
          onClick={() => addDigit(digit)}
          aria-label={letters ? `${digit} ${letters}` : digit}
        >
          <span className={styles.wp2DialKeyNumber}>{digit}</span>
          {!dtmf ? (
            <span className={styles.wp2DialKeyLetters}>
              {letters || "\u00a0"}
            </span>
          ) : null}
        </button>
      ))}
    </div>
  );

  return (
    <div className={styles.wp2Dialer}>
      {!hideStatus && !isReady ? (
        <div className={styles.wp2DialerStatusRow}>
          <span
            className={classNames(
              styles.wp2DialerStatus,
              styles[`wp2DialerStatus_${phase}`]
            )}
            aria-live="polite"
          >
            {phase === "connected" ? "Connected" : statusLabel}
          </span>
        </div>
      ) : null}

      {isReady ? (
        <div
          className={classNames(
            styles.wp2ContactCard,
            styles.wp2ContactCardOpen
          )}
        >
          <div
            className={classNames(styles.wp2ContactCardContent, {
              [styles.wp2ContactCardContentMessagesOnly]: hideDialpad,
            })}
          >
            {!hideDialpad ? (
              <div className={styles.wp2DialerReady}>
                {!hideStatus ? (
                  <div className={styles.wp2DialerHeader}>
                    <span
                      className={classNames(
                        styles.wp2DialerStatus,
                        styles[`wp2DialerStatus_${phase}`]
                      )}
                      aria-live="polite"
                    >
                      {statusLabel}
                    </span>
                  </div>
                ) : null}
                <div className={styles.wp2DialerBody}>
                  <Spin spinning={isNumbersLoading}>
                    <div className={styles.wp2DialerDisplay}>
                      <Input
                        value={phone}
                        readOnly={hideDialpad}
                        onChange={(event) =>
                          onPhoneChange(formatDialpadPhone(event.target.value))
                        }
                        aria-label="Patient phone number"
                        placeholder="Enter patient phone number"
                      />
                      {!hideDialpad && !circularCallButton ? (
                        <Button
                          type="text"
                          icon={<ArrowLeftOutlined />}
                          onClick={removeDigit}
                          disabled={!phone}
                          aria-label="Delete last digit"
                        />
                      ) : null}
                    </div>
                  </Spin>

                  {!reserveSuggestionSpace ? renderDialerMeta() : null}

                  {reserveSuggestionSpace ||
                  (contactSuggestions.length > 0 && onSelectSuggestion) ? (
                    <div
                      className={classNames(styles.wp2DialSuggestionsSlot, {
                        [styles.wp2DialSuggestionsSlotReserved]:
                          reserveSuggestionSpace,
                      })}
                    >
                      {reserveSuggestionSpace ? renderDialerMeta() : null}
                      {contactSuggestions.length > 0 && onSelectSuggestion ? (
                        <div
                          className={styles.wp2DialSuggestions}
                          aria-label="Matching contacts"
                        >
                          {contactSuggestions.map((suggestion) => (
                            <button
                              type="button"
                              key={suggestion.key}
                              className={classNames(styles.wp2DialSuggestion, {
                                [styles.wp2DialSuggestionAction]:
                                  suggestion.actionLabel,
                              })}
                              onClick={
                                suggestion.onClick ??
                                (() =>
                                  onSelectSuggestion(suggestion.phoneNumber))
                              }
                            >
                              {suggestion.actionLabel ? (
                                <span>{suggestion.actionLabel}</span>
                              ) : (
                                <>
                                  <span
                                    className={styles.wp2DialSuggestionName}
                                  >
                                    {suggestion.name}
                                  </span>
                                  <span
                                    className={styles.wp2DialSuggestionNumber}
                                  >
                                    {suggestion.description
                                      ? `${suggestion.description} · `
                                      : ""}
                                    {formatDialpadPhone(suggestion.phoneNumber)}
                                  </span>
                                </>
                              )}
                            </button>
                          ))}
                        </div>
                      ) : null}
                    </div>
                  ) : null}

                  {!hideDialpad ? renderKeypad() : null}
                </div>

                {!hideDialpad ? (
                  <div
                    className={classNames(styles.wp2ContactActions, {
                      [styles.wp2ContactActionsCircular]: circularCallButton,
                    })}
                  >
                    <Button
                      type="primary"
                      icon={<PhoneOutlined />}
                      shape={circularCallButton ? "circle" : undefined}
                      disabled={!canCall || !hasDialableNumber}
                      onClick={onStartCall}
                      className={classNames(styles.wp2CallButton, {
                        [styles.wp2CallButtonCircular]: circularCallButton,
                      })}
                      aria-label="Call"
                    >
                      {circularCallButton ? null : "Call"}
                    </Button>
                    {circularCallButton ? (
                      <Button
                        type="text"
                        shape="circle"
                        icon={<ArrowLeftOutlined />}
                        onClick={removeDigit}
                        disabled={!phone}
                        className={styles.wp2BackspaceButtonCircular}
                        aria-label="Delete last digit"
                      />
                    ) : null}
                  </div>
                ) : null}
              </div>
            ) : null}
            {!hideMessages ? (
              <aside className={styles.wp2MessagesPanel} aria-hidden={false}>
                <div className={styles.wp2MessagesPanelInner}>
                  <div className={styles.wp2MessagesHeader}>
                    <span>Message {formatDialpadPhone(phone)}</span>
                  </div>
                  <div className={styles.wp2MessagesBody}>
                    {hasMessageHistory ? (
                      <>
                        <div className={styles.wp2MessageThreadViewport}>
                          <Spin spinning={isSmsLoading}>
                            <div
                              className={styles.wp2MessageThread}
                              ref={threadRef}
                            >
                              {smsThread?.messages.map((item) => {
                                const outbound = item.direction === "outbound";
                                return (
                                  <div
                                    key={item.id}
                                    className={classNames(
                                      styles.wp2MessageBubble,
                                      {
                                        [styles.wp2MessageBubbleOutbound]:
                                          outbound,
                                      }
                                    )}
                                  >
                                    <span>
                                      {item.body || "(empty message)"}
                                    </span>
                                    <small>
                                      {outbound ? "Sent" : "Received"}
                                      {item.created_at
                                        ? ` — ${
                                            timeProvider
                                              .convertIntoLocalDateTime(
                                                item.created_at
                                              )
                                              ?.format("MMM D, YYYY h:mm A") ??
                                            ""
                                          }`
                                        : ""}
                                      {item.status &&
                                      item.status.toLowerCase() !== "queued"
                                        ? ` — ${item.status}`
                                        : ""}
                                    </small>
                                  </div>
                                );
                              })}
                            </div>
                          </Spin>
                        </div>
                        <div className={styles.wp2MessageTemplates}>
                          {MESSAGE_TEMPLATES.map((template) => (
                            <button
                              type="button"
                              key={template.label}
                              onClick={() => setTextBody(template.body)}
                            >
                              {template.label}
                            </button>
                          ))}
                        </div>
                      </>
                    ) : (
                      <Spin spinning={isSmsLoading}>
                        <div className={styles.wp2EmptyMessageThread}>
                          <strong>
                            Send {patientFirstName} their first text
                          </strong>
                          <p>
                            No message history with this number. Start with a
                            template or write your own below.
                          </p>
                          <div className={styles.wp2EmptyMessageTemplates}>
                            {MESSAGE_TEMPLATES.map((template) => (
                              <button
                                type="button"
                                key={template.label}
                                onClick={() => setTextBody(template.body)}
                              >
                                <span
                                  className={styles.wp2EmptyMessageTemplateIcon}
                                >
                                  {templateIcon(template.icon)}
                                </span>
                                <span>
                                  <strong>{template.label}</strong>
                                  <small>{template.description}</small>
                                </span>
                              </button>
                            ))}
                          </div>
                        </div>
                      </Spin>
                    )}
                  </div>
                  <div className={styles.wp2MessageComposer}>
                    <div className={styles.wp2MessageComposerInput}>
                      <Input.TextArea
                        value={textBody}
                        onChange={(event) => setTextBody(event.target.value)}
                        placeholder="Text message…"
                        autoSize={
                          hideDialpad
                            ? { minRows: 2, maxRows: 8 }
                            : { minRows: 2, maxRows: 4 }
                        }
                      />
                      <div className={styles.wp2MessageComposerMeta}>
                        <span>
                          {messageCount} / 160
                          {smsSegments > 1 ? ` — ${smsSegments} SMS` : ""}
                        </span>
                      </div>
                      <Button
                        type="primary"
                        shape="circle"
                        icon={<SendOutlined />}
                        loading={isSendingText}
                        disabled={!hasDialableNumber || !textBody.trim()}
                        onClick={() => void handleSendText()}
                        aria-label="Send message"
                        className={styles.wp2MessageSendButton}
                      />
                    </div>
                  </div>
                </div>
              </aside>
            ) : null}
          </div>
        </div>
      ) : null}

      {isActive ? (
        <div className={styles.wp2DialerLive}>
          <div
            className={classNames(styles.wp2CallAvatar, {
              [styles.wp2CallAvatarRinging]: !isConnected,
            })}
          >
            {initials}
          </div>
          <div className={styles.wp2CallPatientName}>{patientName}</div>
          <div className={styles.wp2CallPatientPhone}>
            {formatDialpadPhone(phone)}
          </div>
          <div className={styles.wp2CallTimer}>
            {isConnected ? formatCallDuration(elapsedSeconds) : "Calling…"}
          </div>

          <div
            className={classNames(styles.wp2CallWave, {
              [styles.wp2CallWaveHidden]: !isConnected,
              [styles.wp2CallWaveMuted]: isMuted,
            })}
            aria-label="Live call audio activity"
          >
            {WAVE_FACTORS.map((factor, index) => (
              <span
                key={index}
                style={{ height: `${4 + waveLevel * factor * 24}px` }}
              />
            ))}
          </div>

          {showDtmf ? renderKeypad(true) : null}

          <div className={styles.wp2CallControls}>
            <div className={styles.wp2CallControl}>
              <button
                type="button"
                className={classNames(styles.wp2CallControlButton, {
                  [styles.wp2CallControlButtonActive]: isMuted,
                })}
                onClick={onToggleMute}
                disabled={!isConnected}
                aria-label={isMuted ? "Unmute" : "Mute"}
                aria-pressed={isMuted}
              >
                {isMuted ? <AudioMutedOutlined /> : <AudioOutlined />}
              </button>
              <span>{isMuted ? "Unmute" : "Mute"}</span>
            </div>
            <div className={styles.wp2CallControl}>
              <button
                type="button"
                className={classNames(styles.wp2CallControlButton, {
                  [styles.wp2CallControlButtonActive]: showDtmf,
                })}
                onClick={() => setShowDtmf((current) => !current)}
                disabled={!isConnected}
                aria-label="Toggle keypad"
                aria-pressed={showDtmf}
              >
                <AppstoreOutlined />
              </button>
              <span>Keypad</span>
            </div>
            <div className={styles.wp2CallControl}>
              <button
                type="button"
                className={classNames(
                  styles.wp2CallControlButton,
                  styles.wp2CallEndButton
                )}
                onClick={onHangUp}
                aria-label="Hang up"
              >
                <DisconnectOutlined />
              </button>
              <span>End</span>
            </div>
          </div>
        </div>
      ) : null}

      {isEnded ? (
        <div className={styles.wp2DialerEnded}>
          <div className={styles.wp2CallEndedIcon}>
            <DisconnectOutlined />
          </div>
          <div className={styles.wp2CallEndedTitle}>
            {phase === "error"
              ? "Call failed"
              : phase === "busy"
              ? "Line busy"
              : phase === "rejected"
              ? "Call not answered"
              : "Call ended"}
          </div>
          <div className={styles.wp2CallEndedSummary}>
            <span>{patientName}</span>
            <span>
              {phase === "busy"
                ? "The recipient line was busy"
                : wasConnected
                ? formatCallDuration(elapsedSeconds)
                : "No answer"}
            </span>
          </div>
          {errorMessage ? (
            <div className={styles.wp2DialerError} role="alert">
              {errorMessage}
            </div>
          ) : null}
          <div className={styles.wp2DialerEndedActions}>
            <Button onClick={onReset}>Back</Button>
            <Button type="primary" onClick={onStartCall}>
              Call Again
            </Button>
          </div>
        </div>
      ) : null}
    </div>
  );
};
